import { z } from 'zod';

/**
 * AI Analyst ("Analytics Reporter") schemas.
 *
 * A report is generated for a *preset* (a "lens") that defines a binary
 * outcome split over a population of calls — e.g. payment links that were
 * clicked & paid vs. clicked but not paid. The pipeline computes deterministic
 * stats in code, then an LLM reads the transcripts and produces the narrative
 * (findings + recommendations) — mirroring the manual analyst report.
 */

// ---------------------------------------------------------------------------
// Presets (the available "lenses"). Adding a preset here + a stats builder on
// the backend is all it takes to support a new outcome split.
// ---------------------------------------------------------------------------
export const ANALYST_PRESETS = [
  {
    key: 'payment_link_conversion',
    label: 'Payment links — clicked vs paid',
    description:
      'Why clicked payment links convert (or not). Compares calls where the link was clicked and paid against clicked-but-not-paid.',
    successLabel: 'Clicked & paid',
    failureLabel: 'Clicked, not paid',
  },
] as const;

export const analystPresetKeySchema = z.enum(
  ANALYST_PRESETS.map((p) => p.key) as [string, ...string[]],
);
export type AnalystPresetKey = (typeof ANALYST_PRESETS)[number]['key'];

export const ANALYST_LLM_PROVIDERS = ['gemini', 'anthropic'] as const;
export const analystLlmProviderSchema = z.enum(ANALYST_LLM_PROVIDERS);
export type AnalystLlmProvider = (typeof ANALYST_LLM_PROVIDERS)[number];

// Selectable models per provider (the per-report model picker reads these).
export const ANALYST_MODELS: Record<AnalystLlmProvider, { value: string; label: string }[]> = {
  gemini: [
    { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  ],
  anthropic: [
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  ],
};

export const ANALYST_DEFAULT_PROVIDER: AnalystLlmProvider = 'gemini';
export const ANALYST_DEFAULT_MODEL: Record<AnalystLlmProvider, string> = {
  gemini: 'gemini-3.1-pro',
  anthropic: 'claude-sonnet-4-6',
};

export const analystReportStatusSchema = z.enum(['PENDING', 'GENERATING', 'COMPLETED', 'FAILED']);
export type AnalystReportStatus = z.infer<typeof analystReportStatusSchema>;

// ---------------------------------------------------------------------------
// Deterministic stats (computed in code, not by the LLM).
// ---------------------------------------------------------------------------
export const analystBreakdownRowSchema = z.object({
  label: z.string(),
  total: z.number(),
  success: z.number(),
  conversion_pct: z.number(),
});
export type AnalystBreakdownRow = z.infer<typeof analystBreakdownRowSchema>;

export const analystStatsSchema = z.object({
  success_label: z.string(),
  failure_label: z.string(),
  total: z.number(),
  success_count: z.number(),
  failure_count: z.number(),
  success_share_pct: z.number(),
  // For the failure group, why the payment never landed at the rail level
  // (e.g. SENT vs FAILED) — distinguishes behavioural drop-off from tech.
  failure_status_breakdown: z.array(z.object({ label: z.string(), count: z.number() })),
  by_channel: z.array(analystBreakdownRowSchema),
  by_configuration: z.array(analystBreakdownRowSchema),
  by_direction: z.array(analystBreakdownRowSchema),
  timing: z.object({
    median_secs_to_action_success: z.number().nullable(),
    median_secs_link_to_click_success: z.number().nullable(),
    median_secs_link_to_click_failure: z.number().nullable(),
  }),
  amount: z.object({
    distinct_amounts: z.array(z.number()),
    currency: z.string().nullable(),
  }),
});
export type AnalystStats = z.infer<typeof analystStatsSchema>;

// ---------------------------------------------------------------------------
// LLM-produced narrative (the qualitative analysis).
// ---------------------------------------------------------------------------
export const analystFindingSchema = z.object({
  title: z.string(),
  detail: z.string(),
});

export const analystReasonSchema = z.object({
  reason: z.string(),
  count: z.number(),
  share_pct: z.number(),
  nature: z.string(), // e.g. "Fixable in script", "Process gap", "Genuine no"
});

export const analystRecommendationSchema = z.object({
  priority: z.number(), // 1 = highest
  title: z.string(),
  detail: z.string(),
});

export const analystNarrativeSchema = z.object({
  headline: z.string(),
  executive_summary: z.array(z.string()), // paragraphs
  key_findings: z.array(analystFindingSchema),
  reason_breakdown: z.array(analystReasonSchema),
  recommendations: z.array(analystRecommendationSchema),
  bottom_line: z.string(),
});
export type AnalystNarrative = z.infer<typeof analystNarrativeSchema>;

// ---------------------------------------------------------------------------
// Stored params (what the report was generated over).
// ---------------------------------------------------------------------------
export const analystReportParamsSchema = z.object({
  preset_key: analystPresetKeySchema,
  date_from: z.string().nullable(),
  date_to: z.string().nullable(),
  sample_size: z.number().nullable(),
  // Optional scoping to a configuration (and a specific version) so the analyst
  // can read the agent's actual prompts and tie failures to prompt instructions.
  configuration_id: z.string().nullable().optional(),
  configuration_version_id: z.string().nullable().optional(),
  configuration_label: z.string().nullable().optional(),
});
export type AnalystReportParams = z.infer<typeof analystReportParamsSchema>;

// ---------------------------------------------------------------------------
// Stored record.
// ---------------------------------------------------------------------------
export const analystReportRecordSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  preset_key: z.string(),
  status: analystReportStatusSchema,
  params: analystReportParamsSchema,
  stats: analystStatsSchema.nullable(),
  narrative: analystNarrativeSchema.nullable(),
  llm_provider: z.string().nullable(),
  llm_model: z.string().nullable(),
  error_message: z.string().nullable(),
  created_at: z.string().datetime().or(z.date()),
  updated_at: z.string().datetime().or(z.date()),
});
export type AnalystReportRecord = z.infer<typeof analystReportRecordSchema>;

// ---------------------------------------------------------------------------
// Create DTO.
// ---------------------------------------------------------------------------
export const createAnalystReportDtoSchema = z.object({
  preset_key: analystPresetKeySchema,
  title: z.string().min(1).max(255).optional(),
  date_from: z.coerce.date().optional(),
  date_to: z.coerce.date().optional(),
  provider: analystLlmProviderSchema.optional(),
  // Per-report model override (must belong to the chosen provider). Defaults to
  // that provider's default model when omitted.
  model: z.string().min(1).max(100).optional(),
  // Scope the analysis to one configuration / version (also injects its prompts).
  configuration_id: z.string().uuid().optional(),
  configuration_version_id: z.string().uuid().optional(),
});
export type CreateAnalystReportDto = z.infer<typeof createAnalystReportDtoSchema>;

// ---------------------------------------------------------------------------
// List query.
// ---------------------------------------------------------------------------
export const analystReportQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  preset_key: analystPresetKeySchema.optional(),
});
export type AnalystReportQuery = z.infer<typeof analystReportQuerySchema>;
